/**
 * GET `{VITE_API_BASE_URL}/tenant-core/activity-logs/` — staff JWT + tenant.
 */
import { getStaffAccessToken } from "./authApi";
import { formatApiErrorBody } from "./formatApiError";

export const ACTIVITY_LOGS_PATH = "/tenant-core/activity-logs/";

/** Types requested from tenant-core (`?activity_type=…`). Only these rows are loaded. */
export const ACTIVITY_LOG_FETCH_TYPES = ["Edit", "Delete", "Create"] as const;

export type ActivityLogFetchType = (typeof ACTIVITY_LOG_FETCH_TYPES)[number];

function jlApiBase(): string {
  const raw =
    typeof import.meta !== "undefined"
      ? (import.meta.env.VITE_API_BASE_URL as string | undefined)
      : undefined;
  return (raw ?? "").replace(/\/$/, "");
}

function jlTenantHeaders(): Record<string, string> {
  const id =
    typeof import.meta !== "undefined"
      ? (import.meta.env.VITE_X_TENANTID as string | undefined)
      : undefined;
  const trimmed = (id ?? "").replace(/^\uFEFF/, "").trim();
  if (!trimmed) {
    return {};
  }
  return { "X-Tenant-Id": trimmed };
}

function coerceList(raw: unknown): Record<string, unknown>[] {
  if (Array.isArray(raw)) {
    return raw.filter(
      (x): x is Record<string, unknown> =>
        typeof x === "object" && x !== null && !Array.isArray(x)
    );
  }
  if (!raw || typeof raw !== "object") {
    return [];
  }
  const o = raw as Record<string, unknown>;
  for (const key of ["results", "data", "content", "items"] as const) {
    const arr = o[key];
    if (Array.isArray(arr)) {
      return arr.filter(
        (x): x is Record<string, unknown> =>
          typeof x === "object" && x !== null && !Array.isArray(x)
      );
    }
  }
  return [];
}

function apiHostLabel(apiBase: string): string {
  try {
    const u = new URL(apiBase.includes("://") ? apiBase : `https://${apiBase}`);
    return u.host;
  } catch {
    return apiBase.replace(/^https?:\/\//i, "").split("/")[0] || apiBase;
  }
}

/** Browser-only failures (CORS, offline, mixed content). curl is unaffected. */
function networkFetchErrorMessage(apiBase: string, cause: unknown): string {
  const host = apiHostLabel(apiBase);
  const raw =
    cause instanceof Error ? cause.message : String(cause ?? "Network error");
  if (/failed to fetch|load failed|networkerror/i.test(raw)) {
    return `Failed to fetch (${host}). The browser blocked this request — usually CORS or TLS. Ask the API team to allow your site’s origin on OPTIONS + GET for /tenant-core/activity-logs/ (Authorization, X-Tenant-Id).`;
  }
  return raw;
}

/**
 * Paginated `next` links often omit the same path prefix as `VITE_API_BASE_URL` (e.g. `/api`).
 * Building `origin + /tenant-core/...` skips `/api`, hits the wrong origin route, and the
 * browser reports a CORS error while page 1 (built as `base + /tenant-core/...`) succeeds.
 */
function normalizeActivityLogsNextUrl(apiBase: string, nextLink: string): string {
  const baseStr = apiBase.replace(/\/$/, "");
  let resolved: URL;
  try {
    const t = nextLink.trim();
    if (/^https?:\/\//i.test(t)) {
      resolved = new URL(t);
    } else {
      const p = t.startsWith("/") ? t : `/${t}`;
      resolved = new URL(p, baseStr.endsWith("/") ? baseStr : `${baseStr}/`);
    }
  } catch {
    return nextLink.trim();
  }

  let baseUrl: URL;
  try {
    baseUrl = new URL(baseStr.includes("://") ? baseStr : `https://${baseStr}`);
  } catch {
    return resolved.toString();
  }

  const basePath = baseUrl.pathname.replace(/\/$/, "") || "";
  let path = resolved.pathname || "/";
  if (!path.startsWith("/")) {
    path = `/${path}`;
  }
  if (basePath && !path.startsWith(`${basePath}/`) && path !== basePath) {
    path = `${basePath}${path}`;
  }
  const origin =
    resolved.origin === baseUrl.origin ? resolved.origin : baseUrl.origin;
  return `${origin}${path}${resolved.search}${resolved.hash}`;
}

function listNextPageUrl(apiBase: string, raw: unknown): string | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const root = raw as Record<string, unknown>;
  let next: unknown = root.next;
  if (next == null && root.links && typeof root.links === "object") {
    next = (root.links as Record<string, unknown>).next;
  }
  if (next === null || next === undefined || typeof next !== "string") {
    return null;
  }
  const trimmed = next.trim();
  if (!trimmed || trimmed.toLowerCase() === "null") {
    return null;
  }
  return normalizeActivityLogsNextUrl(apiBase, trimmed);
}

async function parseHttpError(res: Response): Promise<string> {
  const fallback = `Request failed (${res.status})`;
  try {
    const t = await res.text();
    const trimmed = t.trim();
    if (!trimmed) {
      return fallback;
    }
    try {
      const j = JSON.parse(trimmed) as unknown;
      return formatApiErrorBody(j) ?? fallback;
    } catch {
      return trimmed.length > 300 ? `${trimmed.slice(0, 300)}…` : trimmed;
    }
  } catch {
    return fallback;
  }
}

function rowDedupeKey(rec: Record<string, unknown>): string {
  const id =
    typeof rec.id === "string"
      ? rec.id
      : typeof rec.uuid === "string"
        ? rec.uuid
        : JSON.stringify(rec).slice(0, 120);
  return `${typeof rec.activity_type === "string" ? rec.activity_type : ""}:${id}`;
}

/**
 * Paginates every page from `initialUrl`, following `next` links from the payload.
 */
async function fetchPaginatedLogs(
  base: string,
  initialUrl: string,
  headers: Record<string, string>
): Promise<{
  logs: Record<string, unknown>[];
  error: string | null;
}> {
  const out: Record<string, unknown>[] = [];
  let url: string | null = initialUrl;
  const maxPages = 200;
  let pages = 0;

  while (url && pages < maxPages) {
    let res: Response;
    try {
      res = await fetch(url, { method: "GET", headers });
    } catch (e) {
      return { logs: out, error: networkFetchErrorMessage(base, e) };
    }
    if (!res.ok) {
      return { logs: out, error: await parseHttpError(res) };
    }

    let raw: unknown;
    try {
      raw = await res.json();
    } catch {
      return {
        logs: out,
        error: "Invalid JSON from activity logs API",
      };
    }

    const list = coerceList(raw);
    out.push(...list);
    url = listNextPageUrl(base, raw);
    pages += 1;
  }

  return { logs: out, error: null };
}

/**
 * Loads Edit / Delete / Create only: one paginated GET per type with `?activity_type=…`.
 */
export async function fetchActivityLogs(): Promise<{
  logs: Record<string, unknown>[];
  error: string | null;
}> {
  const base = jlApiBase();
  const token = getStaffAccessToken();
  if (!base) {
    return { logs: [], error: "Missing VITE_API_BASE_URL" };
  }
  if (!token) {
    return {
      logs: [],
      error: "Sign in with a staff account to load activity logs.",
    };
  }

  // Omit Content-Type on GET — it has no body; sending application/json forces an
  // extra CORS preflight header. Some APIs allow Authorization but not Content-Type
  // on Access-Control-Allow-Headers, which breaks only this client (curl ignores CORS).
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...jlTenantHeaders(),
    Authorization: `Bearer ${token}`,
  };

  const merged: Record<string, unknown>[] = [];
  const seen = new Set<string>();
  const errors: string[] = [];

  for (const activityType of ACTIVITY_LOG_FETCH_TYPES) {
    const qs = new URLSearchParams({ activity_type: activityType }).toString();
    const initialUrl = `${base}${ACTIVITY_LOGS_PATH}?${qs}`;
    const { logs: slice, error } = await fetchPaginatedLogs(base, initialUrl, headers);
    if (error) {
      errors.push(`${activityType}: ${error}`);
      continue;
    }
    for (const rec of slice) {
      const key = rowDedupeKey(rec);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      merged.push(rec);
    }
  }

  if (merged.length === 0 && errors.length > 0) {
    return { logs: [], error: errors[0] ?? "Could not load activity logs." };
  }

  merged.sort((a, b) => {
    const ta =
      typeof a.created_on === "string"
        ? a.created_on
        : typeof a.created_at === "string"
          ? a.created_at
          : "";
    const tb =
      typeof b.created_on === "string"
        ? b.created_on
        : typeof b.created_at === "string"
          ? b.created_at
          : "";
    return tb.localeCompare(ta);
  });

  return { logs: merged, error: null };
}

/**
 * Example request for activity logs filtered by type (`Edit`, `Delete`, `Create` each use this query).
 */
export function getActivityLogsRequestSpec(
  activityType: ActivityLogFetchType = "Edit"
): {
  method: "GET";
  url: string;
  headers: Record<string, string>;
} {
  const base = jlApiBase();
  const qs = new URLSearchParams({ activity_type: activityType }).toString();
  const pathWithQuery = `${ACTIVITY_LOGS_PATH}?${qs}`;
  const url = base ? `${base}${pathWithQuery}` : pathWithQuery;
  const tid =
    typeof import.meta !== "undefined"
      ? (import.meta.env.VITE_X_TENANTID as string | undefined)
      : undefined;
  const tenant = (tid ?? "").replace(/^\uFEFF/, "").trim();
  return {
    method: "GET",
    url,
    headers: {
      Authorization: "Bearer <staff_access_token>",
      Accept: "application/json",
      "X-Tenant-Id": tenant || "(not set — VITE_X_TENANTID)",
    },
  };
}
